import React, { useEffect, useState, useCallback } from 'react';
import { Plus, TrendingUp, Inbox } from 'lucide-react';
import api from '../../lib/api';
import { Spinner } from '../ui/Spinner';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { PeriodSelector } from './PeriodSelector';
import RChart from './RChart';
import LogTradeModal from './LogTradeModal';
import TradeDetailModal from './TradeDetailModal';
import { fmtR, fmtMoney, rColor, fmtTradeDate } from '../../lib/tradeUtils';
import { cn } from '../../lib/utils';

function metricsFor(mode, s) {
  const R = mode === 'R';
  return [
    { label: R ? 'Net R' : 'Net P&L', value: R ? fmtR(s.net_r, 1) : fmtMoney(s.net_pnl, true), raw: R ? s.net_r : s.net_pnl, color: true, key: 'net' },
    { label: 'Win Rate', value: `${(s.win_rate ?? 0).toFixed(1)}%`, key: 'win_rate' },
    { label: 'Avg RR', value: fmtR(s.expectancy, 2), raw: s.expectancy, color: true, key: 'avg_rr' },
    { label: 'Avg Winner', value: R ? fmtR(s.avg_winner, 1) : fmtMoney(s.avg_winner_pnl, true), raw: R ? s.avg_winner : s.avg_winner_pnl, color: true, key: 'avg_winner' },
    { label: 'Avg Loser', value: R ? fmtR(s.avg_loser, 1) : fmtMoney(s.avg_loser_pnl, true), raw: R ? s.avg_loser : s.avg_loser_pnl, color: true, key: 'avg_loser' },
    { label: 'Total Trades', value: `${s.total_trades ?? 0}`, key: 'total_trades' },
  ];
}

export default function TradeJournal() {
  const [period, setPeriod] = useState('this_month');
  const [mode, setMode] = useState('R'); // 'R' | '$'  (journal display only)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [editTrade, setEditTrade] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/trades?period=${period}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (trade) => { setDetailId(null); setEditTrade(trade); setShowLog(true); };
  const closeLog = () => { setShowLog(false); setEditTrade(null); };

  const summary = data?.summary || {};
  const trades = data?.trades || [];
  const metrics = metricsFor(mode, summary);

  return (
    <div className="space-y-6" data-testid="trade-journal">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodSelector value={period} onChange={setPeriod} label={data?.period_label} />
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-white/10 bg-ink-surface p-1" data-testid="journal-mode-toggle">
            {['R', '$'].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`journal-mode-${m === '$' ? 'dollar' : 'r'}`}
                className={cn('rounded-md px-3 py-1.5 text-xs font-bold transition-colors', mode === m ? 'bg-white text-black' : 'text-zinc-400 hover:text-white')}>
                {m === '$' ? '$' : 'R'}
              </button>
            ))}
          </div>
          <Button variant="emerald" onClick={() => { setEditTrade(null); setShowLog(true); }} data-testid="log-trade-button">
            <Plus size={18} /> Log Trade
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid h-64 place-items-center"><Spinner className="h-7 w-7" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {metrics.map((m) => (
              <Card key={m.key} className="p-4" data-testid={`metric-${m.key}`}>
                <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">{m.label}</div>
                <div className={`mt-1.5 font-heading text-2xl font-bold tnum ${m.color ? rColor(m.raw) : 'text-white'}`}>{m.value}</div>
              </Card>
            ))}
          </div>

          <Card className="p-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white">
              <TrendingUp size={16} className="text-emerald" /> Cumulative {mode === '$' ? 'P&L' : 'R'}
            </div>
            {data?.chart?.length ? (
              <RChart data={data.chart} mode={mode} />
            ) : (
              <div className="grid h-48 place-items-center text-sm text-zinc-500">No trades to chart yet.</div>
            )}
          </Card>

          <div className="space-y-2">
            <div className="text-sm font-semibold text-white">Trades</div>
            {trades.length ? (
              trades.map((t) => {
                const val = mode === '$' ? t.pnl : t.r_multiple;
                const display = mode === '$'
                  ? (t.pnl === null ? '—' : fmtMoney(t.pnl, true))
                  : (t.r_multiple === null ? '—' : fmtR(t.r_multiple, 1));
                return (
                  <button key={t.trade_id} onClick={() => setDetailId(t.trade_id)} data-testid={`trade-row-${t.trade_id}`}
                    className="flex w-full items-center gap-4 rounded-xl border border-white/5 bg-ink-card px-4 py-3 text-left transition-colors hover:border-white/20">
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-white">{t.ticker}</div>
                      <div className="truncate text-xs text-zinc-500">{fmtTradeDate(t.traded_at)}</div>
                    </div>
                    {t.r_multiple !== null && (
                      <div className="hidden text-right text-xs text-zinc-500 sm:block">
                        {mode === '$' ? fmtR(t.r_multiple, 1) : (t.pnl !== null ? fmtMoney(t.pnl, true) : '')}
                      </div>
                    )}
                    <div className={`w-24 text-right font-heading text-lg font-bold tnum ${rColor(val)}`} data-testid={`trade-r-${t.trade_id}`}>
                      {display}
                    </div>
                  </button>
                );
              })
            ) : (
              <Card className="grid place-items-center gap-3 p-12 text-center">
                <Inbox className="text-zinc-600" size={28} />
                <div className="text-sm text-zinc-400">No trades logged for this period.</div>
                <Button variant="outline" size="sm" onClick={() => { setEditTrade(null); setShowLog(true); }} data-testid="empty-log-trade">
                  <Plus size={16} /> Log your first trade
                </Button>
              </Card>
            )}
          </div>
        </>
      )}

      <LogTradeModal open={showLog} onClose={closeLog} onSaved={load} trade={editTrade} />
      <TradeDetailModal tradeId={detailId} onClose={() => setDetailId(null)} onEdit={openEdit} onDeleted={() => { setDetailId(null); load(); }} />
    </div>
  );
}
